import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="ai-web-source-status"
//
// Attached to a website source card (#480) while its check or sync is running.
// Polls the status endpoint and reloads the page once the source has settled,
// because the preview and the state panels are rendered server side.
export default class extends Controller {
  static values = {
    url: String,
    interval: { type: Number, default: 3000 },
    // ~10 min budget, afterwards a manual reload remains the fallback.
    maxAttempts: { type: Number, default: 200 },
  };

  connect() {
    this.attempts = 0;
    this.stopped = false;
    this.disconnected = false;
    this.schedule();
  }

  disconnect() {
    this.stopped = true;
    this.disconnected = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.onFocusout) {
      document.removeEventListener("focusout", this.onFocusout);
    }
  }

  schedule() {
    this.timer = setTimeout(() => this.poll(), this.intervalValue);
  }

  async poll() {
    if (this.stopped) return;
    this.attempts += 1;

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (response.ok) {
        const { state, syncing } = await response.json();
        if (this.stopped) return;
        if (!syncing && state !== "checking") return this.settle();
      } else if (isPermanentFailure(response.status)) {
        // Source deleted elsewhere (404) or session gone (401/403): retrying
        // won't help, a manual reload shows the real state.
        this.stopped = true;
        return;
      }
    } catch {
      // Ignore network/transient errors and keep polling.
    }

    if (!this.stopped && this.attempts < this.maxAttemptsValue) this.schedule();
  }

  settle() {
    if (this.disconnected) return;
    // Don't throw away what the admin is typing in another form on the page;
    // reload once focus has left the form fields instead (checked after the
    // focus moved, so tabbing to the next field keeps waiting). Unsaved
    // changes keep it waiting too: saving submits the form and renders the
    // fresh state anyway, reverting the changes lets the next focusout reload.
    if (this.isEditing() || hasUnsavedChanges()) {
      this.onFocusout = () => {
        this.onFocusout = null;
        this.timer = setTimeout(() => this.settle(), 0);
      };
      document.addEventListener("focusout", this.onFocusout, { once: true });
      return;
    }

    this.stopped = true;
    window.location.reload();
  }

  isEditing() {
    const active = document.activeElement;
    return (
      active instanceof HTMLElement &&
      active.matches("input, textarea, select") &&
      active.closest("form") !== null
    );
  }
}

// 4xx means the request itself is refused; 408/429 may pass on a later try.
function isPermanentFailure(status) {
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

function hasUnsavedChanges() {
  return Array.from(document.forms).some((form) =>
    Array.from(form.elements).some(isChanged),
  );
}

function isChanged(element) {
  if (element instanceof HTMLSelectElement) {
    const options = Array.from(element.options);
    // Without a preselected option the browser picks the first enabled one
    // (none for a multi-select).
    const hasDefault = options.some((option) => option.defaultSelected);
    const fallback = element.multiple
      ? null
      : options.find((option) => !option.disabled);
    return options.some(
      (option) =>
        option.selected !==
        (hasDefault ? option.defaultSelected : option === fallback),
    );
  }
  if (element instanceof HTMLInputElement) {
    if (element.type === "checkbox" || element.type === "radio") {
      return element.checked !== element.defaultChecked;
    }
    return element.value !== element.defaultValue;
  }
  if (element instanceof HTMLTextAreaElement) {
    return element.value !== element.defaultValue;
  }
  return false;
}
